import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsPulldownService, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { take } from 'rxjs/operators';
import {
  PaymentsDataService, PaymentOptions, ReceiptQueueQuery, ReceiptQueueRow, ReceiptQueueSummary, ReceiptStage
} from '../../../services/payments-data.service';
import { ReceiptDetailComponent, ReceiptDetailData } from '../receipt-detail/receipt-detail.component';

// =====================================================================
// ReceiptQueueComponent — "Receipts" and "Exceptions" tabs of Customer Payments
// ---------------------------------------------------------------------
// One row per receipt (dbo.payment), from get_payment_queue.
//   mode RECEIPTS    stage buttons: Open (entered + processing) | Processing | Posted | Unapplied cash | All
//   mode EXCEPTIONS  everything with an exception code, incl. STALE (old unposted receipts)
// Server pages at 500 rows (Posted / All hold ~300k receipts) → Previous / Next 500.
// Click a row → receipt pulldown (edit / allocate / post / return / follow-up / history).
// =====================================================================

type QueueMode = 'RECEIPTS' | 'EXCEPTIONS';

interface StageButton { key: ReceiptStage; label: string; }

@Component({
  selector: 'app-receipt-queue',
  imports: [RmsModule],
  templateUrl: './receipt-queue.component.html',
  styleUrl: './receipt-queue.component.scss'
})
export class ReceiptQueueComponent implements OnInit {
  @Input() mode: QueueMode = 'RECEIPTS';
  @Input() options: PaymentOptions | null = null;
  /** Something was saved — the parent refreshes its tab badges */
  @Output() changed = new EventEmitter<void>();

  Loaded = false;
  Loading = false;

  Rows: ReceiptQueueRow[] = [];
  TotalRows = 0;
  Summary: ReceiptQueueSummary | null = null;
  Columns: Column[] = [];
  Buttons: HeaderButton[] = [];

  readonly PageSize = 500;
  PageNumber = 1;

  // Filters
  Stage: ReceiptStage = 'OPEN';
  BankID: number | null = null;
  PaymentMethodID: number | null = null;
  ExceptionCode: string | null = null;
  ReceivedFrom: string | null = null;
  ReceivedTo: string | null = null;
  DepositFrom: string | null = null;
  DepositTo: string | null = null;
  Search = '';
  IncludeStale = false;

  StageButtons: StageButton[] = [
    { key: 'OPEN', label: 'Open' },
    { key: 'PROCESSING', label: 'Processing' },
    { key: 'POSTED', label: 'Posted' },
    { key: 'UNAPPLIED', label: 'Unapplied cash' },
    { key: 'ALL', label: 'All' },
  ];

  BankOptions: { value: number | null; label: string }[] = [];
  MethodOptions: { value: number | null; label: string }[] = [];
  ExceptionOptions: { value: string | null; label: string }[] = [];

  constructor(
    private paymentsData: PaymentsDataService,
    private pulldownService: RmsPulldownService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    if (this.mode === 'EXCEPTIONS') this.Stage = 'EXCEPTIONS';

    const o = this.options;
    this.BankOptions = [{ value: null, label: 'All banks' }, ...(o?.Banks ?? []).map(b => ({ value: b.BankID, label: b.BankName }))];
    this.MethodOptions = [{ value: null, label: 'All methods' }, ...(o?.Methods ?? []).map(m => ({ value: m.PaymentMethodID, label: m.MethodName }))];
    this.ExceptionOptions = [{ value: null, label: 'All exceptions' }, ...(o?.ExceptionCodes ?? []).map(e => ({ value: e.ExceptionCode, label: e.ExceptionName }))];

    this.Columns = this.createColumns();
    this.buildButtons();
    await this.load();
    this.Loaded = true;
    this.cdr.markForCheck();
  }

  get TableId(): string {
    return this.mode === 'EXCEPTIONS' ? 'customer-payments-exceptions' : 'customer-payments-receipts';
  }

  get FirstRow(): number { return this.TotalRows ? (this.PageNumber - 1) * this.PageSize + 1 : 0; }
  get LastRow(): number { return Math.min(this.PageNumber * this.PageSize, this.TotalRows); }
  get HasPrev(): boolean { return this.PageNumber > 1; }
  get HasNext(): boolean { return this.PageNumber * this.PageSize < this.TotalRows; }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  async load() {
    this.Loading = true;
    try {
      const q: ReceiptQueueQuery = {
        Stage: this.Stage,
        BankID: this.BankID,
        PaymentMethodID: this.PaymentMethodID,
        ExceptionCode: this.mode === 'EXCEPTIONS' ? this.ExceptionCode : null,
        ReceivedFrom: this.ReceivedFrom,
        ReceivedTo: this.ReceivedTo,
        DepositFrom: this.DepositFrom,
        DepositTo: this.DepositTo,
        Search: this.Search?.trim() || null,
        IncludeStale: this.IncludeStale,
        PageNumber: this.PageNumber,
        PageSize: this.PageSize
      };
      const r = await lastValueFrom(this.paymentsData.getQueue(q));
      this.Rows = r?.Rows ?? [];
      this.TotalRows = r?.TotalRows ?? 0;
      this.Summary = r?.Summary ?? null;
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the receipts.');
    } finally {
      this.Loading = false;
      this.buildButtons();
      this.cdr.markForCheck();
    }
  }

  async setStage(stage: ReceiptStage) {
    if (this.Stage === stage) return;
    this.Stage = stage;
    this.PageNumber = 1;
    await this.load();
  }

  async applyFilters() {
    this.PageNumber = 1;
    await this.load();
  }

  async clearFilters() {
    this.BankID = null;
    this.PaymentMethodID = null;
    this.ExceptionCode = null;
    this.ReceivedFrom = this.ReceivedTo = this.DepositFrom = this.DepositTo = null;
    this.Search = '';
    this.IncludeStale = false;
    this.PageNumber = 1;
    await this.load();
  }

  async page(delta: number) {
    const next = this.PageNumber + delta;
    if (next < 1 || (delta > 0 && !this.HasNext)) return;
    this.PageNumber = next;
    await this.load();
  }

  // ---------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------

  private buildButtons() {
    const b: HeaderButton[] = [{ label: 'Refresh', onAction: () => this.load() }];
    if (this.mode === 'RECEIPTS') b.push({ label: 'New Receipt', class: 'btn-bg-green', onAction: () => this.openReceipt(null) });
    if (this.TotalRows > this.PageSize) {
      b.push({ label: `◀ Previous ${this.PageSize}`, disabled: !this.HasPrev, onAction: () => this.page(-1) });
      b.push({ label: `Next ${this.PageSize} ▶`, disabled: !this.HasNext, onAction: () => this.page(1) });
    }
    this.Buttons = b;
  }

  createColumns(): Column[] {
    return [
      { key: 'ChargeLogID', label: 'Receipt #', type: 'number', hidden: false },
      {
        key: 'Stage', label: 'Stage', type: 'select', SearchType: 'select', hidden: false,
        cellClass: (row: ReceiptQueueRow) => `q-stage q-stage-${String(row.Stage).toLowerCase()}`
      },
      { key: 'ReceivedDate', label: 'Received', type: 'date', SearchType: 'date', hidden: false },
      { key: 'CompanyName', label: 'Customer', hidden: false },
      { key: 'OrderID', label: 'Order #', type: 'number', hidden: false },
      { key: 'DetailTypeName', label: 'Pay Type', type: 'select', SearchType: 'select', hidden: false },
      { key: 'BankName', label: 'Bank', type: 'select', SearchType: 'select', hidden: false },
      { key: 'GrossAmount', label: 'Amount', type: 'currency', align: 'right', format: '1.2-2', hidden: false },
      { key: 'ProcessorFeeAmount', label: 'Fee', type: 'currency', align: 'right', format: '1.2-2', hidden: false },
      { key: 'AppliedAmount', label: 'Applied', type: 'currency', align: 'right', format: '1.2-2', hidden: false },
      {
        key: 'UnappliedAmount', label: 'Unapplied', type: 'currency', align: 'right', format: '1.2-2', hidden: false,
        cellClass: (row: ReceiptQueueRow) => (row.UnappliedAmount ?? 0) > 0.005 ? 'q-warn' : ''
      },
      { key: 'DepositDate', label: 'Deposit', type: 'date', SearchType: 'date', hidden: false },
      { key: 'PostingDate', label: 'Posted', type: 'date', SearchType: 'date', hidden: false },
      {
        key: 'EffectiveExceptionCode', label: 'Exception', type: 'select', SearchType: 'select', hidden: false,
        cellClass: (row: ReceiptQueueRow) => row.EffectiveExceptionCode ? 'q-bad' : ''
      },
      { key: 'OwnerName', label: 'Owner', hidden: false },
      { key: 'NextAction', label: 'Next Action', hidden: false },
      // extra detail, hidden by default (column picker)
      { key: 'ProcessingDate', label: 'Processing', type: 'date', SearchType: 'date', hidden: true },
      { key: 'MethodName', label: 'Method', type: 'select', SearchType: 'select', hidden: true },
      { key: 'CheckNumber', label: 'Check #', hidden: true },
      { key: 'MaskedReference', label: 'Masked Ref', hidden: true },
      { key: 'ProcessorName', label: 'Processor', hidden: true },
      { key: 'ProcessorReference', label: 'Processor Ref', hidden: true },
      { key: 'CustomerFeeAmount', label: 'Customer Fee', type: 'currency', align: 'right', format: '1.2-2', hidden: true },
      { key: 'NetDepositAmount', label: 'Net Deposit', type: 'currency', align: 'right', format: '1.2-2', hidden: true },
      { key: 'ExceptionNote', label: 'Exception Note', hidden: true },
      { key: 'Notes', label: 'Notes', hidden: true },
    ];
  }

  // ---------------------------------------------------------------------
  // Receipt pulldown
  // ---------------------------------------------------------------------

  openRow(event: { row: any }) {
    this.openReceipt(event?.row?.ChargeLogID ?? null);
  }

  openReceipt(chargeLogId: number | null) {
    const data: ReceiptDetailData = { ChargeLogID: chargeLogId, Options: this.options };
    const ref = this.pulldownService.open(ReceiptDetailComponent, {
      data,
      title: chargeLogId ? `Receipt #${chargeLogId}` : 'New Receipt',
      width: '980px',
      maxWidth: '96vw',
      center: true
    });
    // reload on every close — the pulldown's own "×" closes with no result even after a save
    ref.afterClosed().pipe(take(1)).subscribe(async () => {
      await this.load();
      this.changed.emit();
    });
  }
}
